import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Trophy, Target, Sparkles, Plus, X, Check, Flame, Send, Trash2, CalendarDays, Award } from 'lucide-react';
import api from '../lib/api';
import { CHARACTERS } from '../lib/levels';
import { useAuth } from '../context/AuthContext';

// --- Level meta: color, gradient, icon, question ---
const LEVEL_META = {
  yearly:  { label: 'Yearly',  kicker: 'The Big Dream',   ring: '#F5C518', bg: 'from-[#F5C518] to-[#B8860B]', border: 'border-[#F5C518]', text: 'text-[#8a5a00]', accent: 'bg-[#F5C518]',   icon: Trophy,     leadHint: 3, q: 'What do you want to achieve by the end of this year?' },
  monthly: { label: 'Monthly', kicker: 'The Milestone',   ring: '#B8BFC8', bg: 'from-[#C0C6CE] to-[#7c8592]', border: 'border-[#B8BFC8]', text: 'text-[#4a5563]', accent: 'bg-[#B8BFC8]',   icon: Award,      leadHint: 2, q: "What's one thing you want to accomplish this month?" },
  weekly:  { label: 'Weekly',  kicker: 'The Focus',       ring: '#3B82F6', bg: 'from-[#60A5FA] to-[#1D4ED8]', border: 'border-[#3B82F6]', text: 'text-[#1e3a8a]', accent: 'bg-[#3B82F6]',   icon: Target,     leadHint: 2, q: 'What are you focused on this week?' },
  daily:   { label: 'Daily',   kicker: "Today's Mission", ring: '#10B981', bg: 'from-[#34D399] to-[#047857]', border: 'border-[#10B981]', text: 'text-[#065f46]', accent: 'bg-[#10B981]',   icon: Flame,      leadHint: 3, q: 'What are your 3 most important tasks today?' },
};

const SUBJECT_CATEGORIES = [
  { key: 'academic',   label: 'Academic',   emoji: '📚', options: ['Math', 'Science', 'Reading', 'Writing', 'Languages'] },
  { key: 'personal',   label: 'Personal',   emoji: '🌱', options: ['Kindness', 'Leadership', 'Confidence'] },
  { key: 'health',     label: 'Health',     emoji: '💪', options: ['Exercise', 'Sleep', 'Nutrition'] },
  { key: 'family',     label: 'Family',     emoji: '🏡', options: ['Help at home', 'Quality time'] },
  { key: 'adventure',  label: 'Adventure',  emoji: '🗺️', options: ['Explore', 'Try new things'] },
];

function isoPeriod(level, d = new Date()) {
  if (level === 'daily') return d.toISOString().slice(0, 10);
  if (level === 'monthly') return d.toISOString().slice(0, 7);
  if (level === 'yearly') return String(d.getUTCFullYear());
  // weekly ISO
  const tmp = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = (tmp.getUTCDay() + 6) % 7;
  tmp.setUTCDate(tmp.getUTCDate() - dayNum + 3);
  const firstThu = new Date(Date.UTC(tmp.getUTCFullYear(), 0, 4));
  const week = 1 + Math.round(((tmp - firstThu) / 86400000 - 3 + ((firstThu.getUTCDay() + 6) % 7)) / 7);
  return `${tmp.getUTCFullYear()}-W${String(week).padStart(2, '0')}`;
}

// --- Progress ring ---
const ProgressRing = ({ pct, color, size = 68, stroke = 7, children }) => {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const off = c - (Math.max(0, Math.min(100, pct)) / 100) * c;
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} stroke="#E5E7EB" strokeWidth={stroke} fill="none" />
        <circle cx={size / 2} cy={size / 2} r={r} stroke={color} strokeWidth={stroke} fill="none"
          strokeDasharray={c} strokeDashoffset={off} strokeLinecap="round" className="transition-all duration-500" />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center font-heading font-bold text-navy text-sm">
        {children}
      </div>
    </div>
  );
};

// --- Streak calendar (last 21 days for daily goals) ---
const StreakCalendar = ({ dailyGoals }) => {
  const days = useMemo(() => {
    const list = [];
    for (let i = 20; i >= 0; i--) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      const iso = d.toISOString().slice(0, 10);
      let allDone = false;
      if (dailyGoals.length > 0) {
        allDone = dailyGoals.every((g) => {
          const marks = (g.check_ins || {})[iso] || [];
          const lead = g.leading?.length || 0;
          return lead > 0 && marks.filter(Boolean).length >= lead;
        });
      }
      list.push({ iso, allDone, label: d.getDate() });
    }
    return list;
  }, [dailyGoals]);
  return (
    <div className="grid grid-cols-7 gap-1.5" data-testid="streak-calendar">
      {days.map((d) => (
        <div key={d.iso}
          className={`aspect-square rounded-lg flex items-center justify-center text-[10px] font-heading font-bold border-2 transition-colors ${d.allDone ? 'bg-emerald-500 border-emerald-600 text-white' : 'bg-slate-50 border-slate-200 text-slate-400'}`}
          title={d.iso}
          data-testid={`streak-day-${d.iso}`}>
          {d.label}
        </div>
      ))}
    </div>
  );
};

// --- Goal card ---
const GoalCard = ({ goal, meta, onCheckIn, onDelete, onAchieved }) => {
  const period = isoPeriod(goal.level);
  const marks = (goal.check_ins || {})[period] || [];
  const leadCount = goal.leading?.length || 0;
  const doneNow = marks.filter(Boolean).length;
  const pct = leadCount > 0 ? Math.round((doneNow / leadCount) * 100) : 0;

  return (
    <div className={`relative rounded-3xl border-4 ${meta.border} bg-white shadow-squishy overflow-hidden`} data-testid={`goal-card-${goal.id}`}>
      <div className={`bg-gradient-to-br ${meta.bg} px-5 pt-5 pb-4 text-white`}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-white/80 text-[11px] font-heading font-bold uppercase tracking-wider">{meta.kicker}</div>
            <div className="font-heading text-lg font-bold mt-0.5 leading-tight">{goal.lagging}</div>
            <div className="text-white/85 text-xs mt-1 flex items-center gap-1.5"><span className="opacity-90">Subject:</span> <span className="font-semibold">{goal.subject}</span></div>
          </div>
          <ProgressRing pct={goal.achieved ? 100 : pct} color="#ffffff" size={64} stroke={6}>
            <span className="text-white text-xs">{goal.achieved ? '✓' : `${pct}%`}</span>
          </ProgressRing>
        </div>
      </div>

      <div className="p-5 space-y-2">
        <div className="text-[10px] font-heading font-bold text-navy/60 uppercase tracking-wider mb-1 flex items-center gap-1.5">
          <Sparkles className="w-3 h-3" /> Leading indicators (what YOU control)
        </div>
        {(goal.leading || []).map((txt, i) => {
          const done = !!marks[i];
          return (
            <button key={i} onClick={() => onCheckIn(goal, i, !done)}
              className={`w-full text-left flex items-center gap-3 p-3 rounded-2xl border-2 transition-colors ${done ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200 hover:border-teal/50'}`}
              data-testid={`leading-${goal.id}-${i}`}>
              <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 border-2 ${done ? 'bg-emerald-500 border-emerald-500' : 'bg-white border-slate-300'}`}>
                {done && <Check className="w-4 h-4 text-white" />}
              </div>
              <span className={`text-sm font-semibold ${done ? 'text-navy/70 line-through' : 'text-navy'}`}>{txt}</span>
            </button>
          );
        })}

        <div className="flex items-center justify-between pt-2">
          <button onClick={() => onAchieved(goal, !goal.achieved)}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full font-heading font-bold text-xs transition-colors ${goal.achieved ? 'bg-gold text-navy' : 'bg-slate-100 text-navy/70 hover:bg-gold/30'}`}
            data-testid={`achieve-${goal.id}`}>
            <Trophy className="w-3.5 h-3.5" /> {goal.achieved ? 'Achieved!' : 'Mark as achieved'}
          </button>
          <button onClick={() => onDelete(goal)} className="text-slate-400 hover:text-red-500 transition-colors p-1" data-testid={`delete-goal-${goal.id}`} aria-label="Delete goal">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
      {goal.achieved && (
        <div className="absolute top-2 right-2 bg-gold text-navy rounded-full w-10 h-10 flex items-center justify-center shadow-gold animate-float">
          <Trophy className="w-5 h-5" />
        </div>
      )}
    </div>
  );
};

// --- Create Goal Modal ---
const CreateGoalModal = ({ level, character, onClose, onCreated }) => {
  const meta = LEVEL_META[level];
  const [subject, setSubject] = useState('Math');
  const [lagging, setLagging] = useState('');
  const [leading, setLeading] = useState(Array(meta.leadHint).fill(''));
  const [busy, setBusy] = useState(false);
  const [coachMsg, setCoachMsg] = useState('');
  const [coachInput, setCoachInput] = useState('');
  const [coachBusy, setCoachBusy] = useState(false);

  const askCoach = async () => {
    const text = coachInput.trim() || lagging.trim();
    if (!text) { toast.error('Type your goal or a question first'); return; }
    setCoachBusy(true);
    try {
      const { data } = await api.post('/goals/coach', { text, level, subject });
      setCoachMsg(data.reply);
      setCoachInput('');
    } catch (e) { toast.error('Coach is thinking... try again'); }
    finally { setCoachBusy(false); }
  };

  const submit = async () => {
    const cleanLeading = leading.map((s) => s.trim()).filter(Boolean);
    if (!lagging.trim()) { toast.error('Write your big goal (lagging indicator)'); return; }
    if (cleanLeading.length < 1) { toast.error('Add at least 1 leading indicator'); return; }
    setBusy(true);
    try {
      const { data } = await api.post('/goals', { level, subject, lagging: lagging.trim(), leading: cleanLeading });
      toast.success('Goal set!');
      onCreated(data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to create goal'); }
    finally { setBusy(false); }
  };

  const Icon = meta.icon;

  return (
    <div className="fixed inset-0 z-50 bg-navy/70 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" data-testid="create-goal-modal">
      <div className="bg-white rounded-3xl w-full max-w-lg my-8 shadow-squishyLg overflow-hidden">
        <div className={`bg-gradient-to-br ${meta.bg} p-5 text-white flex items-start justify-between`}>
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-2xl bg-white/20 flex items-center justify-center shrink-0"><Icon className="w-6 h-6" /></div>
            <div>
              <div className="text-white/80 text-[10px] font-heading font-bold uppercase tracking-wider">{meta.kicker}</div>
              <h2 className="font-heading text-xl font-bold leading-tight">{meta.q}</h2>
            </div>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white" aria-label="Close" data-testid="close-goal-modal"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
          {/* Subject */}
          <div>
            <label className="block text-xs font-heading font-bold text-navy/70 uppercase tracking-wider mb-2">Subject</label>
            <div className="flex flex-wrap gap-2">
              {SUBJECT_CATEGORIES.flatMap((c) => c.options.map((o) => ({ cat: c, opt: o }))).map(({ cat, opt }) => (
                <button key={opt} type="button" onClick={() => setSubject(opt)}
                  className={`px-3 py-1.5 rounded-full text-xs font-heading font-bold border-2 transition-colors ${subject === opt ? 'bg-navy text-white border-navy' : 'bg-white text-navy border-slate-200 hover:border-teal'}`}
                  data-testid={`subject-${opt}`}>
                  <span className="mr-1">{cat.emoji}</span>{opt}
                </button>
              ))}
            </div>
          </div>

          {/* Lagging */}
          <div>
            <label className="block text-xs font-heading font-bold text-navy/70 uppercase tracking-wider mb-1">Big result (lagging) — the outcome you want</label>
            <input type="text" value={lagging} onChange={(e) => setLagging(e.target.value)}
              placeholder={level === 'daily' ? "e.g. Finish today's homework" : level === 'weekly' ? 'e.g. Score 100% on Friday quiz' : level === 'monthly' ? 'e.g. Read 4 chapter books' : 'e.g. Get an A in math this year'}
              className="w-full h-12 px-4 rounded-2xl border-2 border-slate-200 focus:border-teal focus:outline-none font-body text-sm"
              maxLength={200}
              data-testid="lagging-input" />
          </div>

          {/* Leading */}
          <div>
            <label className="block text-xs font-heading font-bold text-navy/70 uppercase tracking-wider mb-1 flex items-center gap-1.5">
              <Sparkles className="w-3 h-3 text-teal" /> Leading — the actions YOU control ({meta.leadHint} recommended)
            </label>
            <div className="space-y-2">
              {leading.map((v, i) => (
                <input key={i} type="text" value={v} onChange={(e) => setLeading((arr) => arr.map((x, j) => j === i ? e.target.value : x))}
                  placeholder={i === 0 ? 'e.g. Practice 20 problems every day' : i === 1 ? 'e.g. Ask 1 question in class daily' : 'e.g. Review notes each evening'}
                  className="w-full h-11 px-3 rounded-2xl border-2 border-slate-200 focus:border-teal focus:outline-none font-body text-sm"
                  maxLength={140}
                  data-testid={`leading-input-${i}`} />
              ))}
              <div className="flex gap-2">
                {leading.length < 5 && (
                  <button type="button" onClick={() => setLeading((arr) => [...arr, ''])}
                    className="text-xs font-heading font-bold text-teal hover:text-navy transition-colors inline-flex items-center gap-1" data-testid="add-leading">
                    <Plus className="w-3 h-3" /> Add another action
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Tutor helper */}
          <div className="rounded-2xl border-2 border-dashed border-teal/40 bg-teal/5 p-3">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-full overflow-hidden border-2 border-teal shrink-0">
                <img src={character.image} alt={character.name} className="w-full h-full object-cover" />
              </div>
              <div className="text-xs font-heading font-bold text-navy">Ask {character.name} for help</div>
            </div>
            {coachMsg && (
              <div className="text-sm text-navy bg-white rounded-xl p-2.5 mb-2 border border-teal/30" data-testid="coach-reply">{coachMsg}</div>
            )}
            <div className="flex gap-2">
              <input type="text" value={coachInput} onChange={(e) => setCoachInput(e.target.value)}
                placeholder="e.g. Is my goal SMART enough?"
                className="flex-1 h-10 px-3 rounded-xl border-2 border-slate-200 focus:border-teal focus:outline-none font-body text-sm"
                data-testid="coach-input" />
              <button type="button" onClick={askCoach} disabled={coachBusy}
                className="px-3 rounded-xl bg-teal text-white font-heading font-bold text-xs disabled:opacity-60" data-testid="ask-coach">
                {coachBusy ? '…' : <Send className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div className="rounded-2xl bg-navy text-white p-3 text-xs leading-relaxed">
            <span className="font-heading font-bold text-gold">Coach's rule:</span> You can't fully control the <span className="font-bold">result</span> (lagging), but you CAN control your <span className="font-bold">daily actions</span> (leading). Focus there.
          </div>

          <button onClick={submit} disabled={busy}
            className="w-full h-12 rounded-full bg-gold text-navy font-heading font-bold shadow-gold disabled:opacity-60 inline-flex items-center justify-center gap-2"
            data-testid="submit-goal">
            <Trophy className="w-4 h-4" /> {busy ? 'Setting goal…' : 'Set this goal'}
          </button>
        </div>
      </div>
    </div>
  );
};

// --- Main GoalsView ---
export default function GoalsView({ readOnly = false, initialData = null, title = 'My Goals' }) {
  const { user } = useAuth();
  const character = CHARACTERS[user?.character] || CHARACTERS.ouistiti;
  const [goals, setGoals] = useState({ yearly: [], monthly: [], weekly: [], daily: [] });
  const [streak, setStreak] = useState(0);
  const [loading, setLoading] = useState(!initialData);
  const [createLevel, setCreateLevel] = useState(null);
  const [greeted, setGreeted] = useState(false);
  const [greeting, setGreeting] = useState('');

  const load = async () => {
    try {
      const { data } = await api.get('/goals');
      setGoals(data.goals);
      setStreak(data.streak || 0);
    } catch (e) { toast.error('Failed to load goals'); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (initialData) {
      setGoals(initialData.goals);
      setStreak(initialData.streak || 0);
    } else {
      load();
    }
  }, [initialData]);

  useEffect(() => {
    if (readOnly || greeted) return;
    setGreeted(true);
    (async () => {
      try {
        const { data } = await api.post('/goals/coach', { text: "Greet me and remind me of the leading vs lagging concept in one short paragraph. I just opened the Goals area.", level: 'daily' });
        setGreeting(data.reply);
      } catch { /* silent */ }
    })();
  }, [readOnly, greeted]);

  const handleCheckIn = async (goal, idx, completed) => {
    // Optimistic update
    setGoals((prev) => {
      const copy = { ...prev, [goal.level]: prev[goal.level].map((g) => {
        if (g.id !== goal.id) return g;
        const period = isoPeriod(g.level);
        const marks = [...((g.check_ins || {})[period] || Array(g.leading.length).fill(false))];
        while (marks.length < g.leading.length) marks.push(false);
        marks[idx] = completed;
        return { ...g, check_ins: { ...(g.check_ins || {}), [period]: marks } };
      }) };
      return copy;
    });
    try {
      const { data } = await api.post(`/goals/${goal.id}/checkin`, { leading_index: idx, completed });
      setGoals((prev) => ({ ...prev, [goal.level]: prev[goal.level].map((g) => g.id === goal.id ? data : g) }));
      if (goal.level === 'daily') {
        // Refresh streak
        try { const { data: s } = await api.get('/goals/streak'); setStreak(s.streak || 0); } catch {}
      }
      if (completed) toast.success('Nice — action locked in ✓');
    } catch (e) {
      toast.error('Failed to save');
      load();
    }
  };

  const handleAchieved = async (goal, achieved) => {
    try {
      const { data } = await api.patch(`/goals/${goal.id}`, { achieved });
      setGoals((prev) => ({ ...prev, [goal.level]: prev[goal.level].map((g) => g.id === goal.id ? data : g) }));
      if (achieved) toast.success('🏆 Goal achieved — that\'s champion energy!');
    } catch (e) { toast.error('Failed to update'); }
  };

  const handleDelete = async (goal) => {
    if (!window.confirm('Delete this goal?')) return;
    try {
      await api.delete(`/goals/${goal.id}`);
      setGoals((prev) => ({ ...prev, [goal.level]: prev[goal.level].filter((g) => g.id !== goal.id) }));
      toast.success('Goal removed');
    } catch (e) { toast.error('Failed'); }
  };

  if (loading) return <div className="p-6 text-center text-navy/60 font-heading">Loading goals…</div>;

  return (
    <div className="space-y-5" data-testid="goals-view">
      {/* Header + streak */}
      <section className="rounded-3xl bg-gradient-to-br from-navy via-navy to-teal text-white p-5 shadow-squishyLg" data-testid="goals-header">
        <div className="flex items-start gap-4">
          <div className="w-16 h-16 rounded-full overflow-hidden border-4 border-gold shrink-0 bg-white animate-float">
            <img src={character.image} alt={character.name} className="w-full h-full object-cover" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-gold text-[11px] font-heading font-bold uppercase tracking-wider">{title}</div>
            <h2 className="font-heading text-2xl font-bold leading-tight">Set goals. Track actions. Win.</h2>
            <p className="text-white/85 text-sm mt-1 leading-snug">{greeting || 'You can\'t control the result (lagging). You CAN control your daily actions (leading). Focus there.'}</p>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-3">
          <div className="rounded-2xl bg-white/10 border border-white/20 p-3 flex items-center gap-3" data-testid="streak-card">
            <Flame className="w-8 h-8 text-orange-300 shrink-0" />
            <div>
              <div className="font-heading text-3xl font-bold text-white">{streak}</div>
              <div className="text-white/80 text-[11px] font-heading font-bold uppercase tracking-wider">day streak</div>
            </div>
          </div>
          <div className="rounded-2xl bg-white/10 border border-white/20 p-3 flex items-center gap-3">
            <Trophy className="w-8 h-8 text-gold shrink-0" />
            <div>
              <div className="font-heading text-3xl font-bold text-white" data-testid="achieved-count">
                {Object.values(goals).flat().filter((g) => g.achieved).length}
              </div>
              <div className="text-white/80 text-[11px] font-heading font-bold uppercase tracking-wider">goals achieved</div>
            </div>
          </div>
        </div>
        <div className="mt-4">
          <div className="text-white/80 text-[11px] font-heading font-bold uppercase tracking-wider mb-2 flex items-center gap-1.5">
            <CalendarDays className="w-3.5 h-3.5" /> Last 21 days
          </div>
          <StreakCalendar dailyGoals={goals.daily} />
        </div>
      </section>

      {/* Level sections */}
      {['daily', 'weekly', 'monthly', 'yearly'].map((level) => {
        const meta = LEVEL_META[level];
        const items = goals[level] || [];
        const Icon = meta.icon;
        return (
          <section key={level} className="bg-white rounded-3xl p-5 shadow-squishy border-2 border-cream" data-testid={`goals-section-${level}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-2xl bg-gradient-to-br ${meta.bg} flex items-center justify-center shadow-squishy`}>
                  <Icon className="w-5 h-5 text-white" />
                </div>
                <div>
                  <div className={`font-heading text-lg font-bold ${meta.text}`}>{meta.label}</div>
                  <div className="text-navy/60 text-xs">{meta.kicker}</div>
                </div>
              </div>
              {!readOnly && (
                <button onClick={() => setCreateLevel(level)}
                  className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-navy text-white font-heading font-bold text-xs hover:scale-[1.03] transition-transform`}
                  data-testid={`add-goal-${level}`}>
                  <Plus className="w-3.5 h-3.5" /> New {meta.label} Goal
                </button>
              )}
            </div>

            {items.length === 0 ? (
              <div className={`rounded-2xl border-2 border-dashed ${meta.border} p-5 text-center`}>
                <div className={`w-12 h-12 mx-auto rounded-full bg-gradient-to-br ${meta.bg} flex items-center justify-center mb-2 opacity-80`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
                <p className="text-navy font-heading font-bold text-sm mb-0.5">{meta.q}</p>
                <p className="text-navy/60 text-xs">{readOnly ? 'No goal yet.' : `Tap "New ${meta.label} Goal" to write one.`}</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {items.map((g) => (
                  <GoalCard key={g.id} goal={g} meta={meta}
                    onCheckIn={readOnly ? () => {} : handleCheckIn}
                    onDelete={readOnly ? () => {} : handleDelete}
                    onAchieved={readOnly ? () => {} : handleAchieved}
                  />
                ))}
              </div>
            )}
          </section>
        );
      })}

      {createLevel && (
        <CreateGoalModal level={createLevel} character={character}
          onClose={() => setCreateLevel(null)}
          onCreated={(g) => {
            setGoals((prev) => ({ ...prev, [g.level]: [g, ...prev[g.level]] }));
            setCreateLevel(null);
          }} />
      )}
    </div>
  );
}
